import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Building2, DollarSign, Users, TrendingUp, LogOut, Play, Pause, Plus, Edit, Percent, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_PLANS = {
  starter: { name: 'Starter', price: 55, limit: 8 },
  small_business: { name: 'Small Business', price: 125, limit: 20 },
  mid_size: { name: 'Mid Size', price: 250, limit: 45 },
  big_business: { name: 'Big Business', price: 350, limit: 75 },
  enterprise: { name: 'Enterprise', price: 'Contact', limit: 999999 }
};

const AVAILABLE_FEATURES = {
  // Starter features (included by default)
  basic_scheduling: { name: 'Basic Scheduling', tier: 'starter' },
  time_tracking: { name: 'Time Tracking', tier: 'starter' },
  
  // Small Business features
  project_management: { name: 'Project Management', tier: 'small_business' },
  project_communication: { name: 'Project Communication', tier: 'small_business' },
  upload_pictures: { name: 'Upload Pictures', tier: 'small_business' },
  
  // Mid Size features
  material_ordering: { name: 'Material Ordering', tier: 'mid_size' },
  stock_management: { name: 'Stock Management', tier: 'mid_size' },
  expense_reimbursement: { name: 'Expense Reimbursement', tier: 'mid_size' },
  
  // Big Business features
  sub_projects: { name: 'Sub-projects', tier: 'big_business' },
  material_returns: { name: 'Material Returns', tier: 'big_business' },
  material_transfers: { name: 'Material Transfers', tier: 'big_business' },
  upload_videos: { name: 'Upload Videos', tier: 'big_business' },
  subcontractors: { name: 'Subcontractors', tier: 'big_business' },
  upload_invoices: { name: 'Upload Invoices', tier: 'big_business' },
  equipment_management: { name: 'Equipment Management', tier: 'big_business' },
  payroll_summary: { name: 'Payroll Summary', tier: 'big_business' },
  excel_export: { name: 'Excel Export', tier: 'big_business' },
  
  // Mid Size features (PWA)
  pwa_install: { name: 'Mobile App (PWA)', tier: 'mid_size' },
  push_notifications: { name: 'Push Notifications', tier: 'mid_size' }
};

const BILLING_CYCLES = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual'
};

export default function SuperAdminDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [signups, setSignups] = useState([]);
  const [activeTab, setActiveTab] = useState('companies');
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [showCustomFeaturesDialog, setShowCustomFeaturesDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_phone: '',
    plan: 'starter'
  });
  
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan: '',
    billing_cycle: 'monthly'
  });
  
  const [discountForm, setDiscountForm] = useState({
    discount_percentage: 0
  });

  const [adminForm, setAdminForm] = useState({
    new_email: '',
    new_password: '',
    new_name: '',
    new_phone: ''
  });
  
  const [adminDetails, setAdminDetails] = useState(null);
  const [pwaStats, setPwaStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [analyticsRes, companiesRes, signupsRes, pwaStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/superadmin/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/superadmin/companies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/superadmin/signups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/superadmin/pwa-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setCompanies(data);
      }

      if (signupsRes.ok) {
        const data = await signupsRes.json();
        setSignups(data);
      }

      if (pwaStatsRes.ok) {
        const data = await pwaStatsRes.json();
        setPwaStats(data);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/superadmin/companies/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Company created successfully!');
        toast.info(`Admin Password: ${data.admin.password} - Share this with the admin`);
        setShowCreateDialog(false);
        setCreateForm({
          company_name: '',
          admin_name: '',
          admin_email: '',
          admin_password: '',
          admin_phone: '',
          plan: 'starter'
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create company');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handlePauseResume = async (companyId, currentStatus) => {
    const token = localStorage.getItem('token');
    const action = currentStatus === 'grace_period' || currentStatus === 'suspended' ? 'resume' : 'pause';
    
    try {
      const response = await fetch(
        `${API_URL}/api/superadmin/companies/${companyId}/${action}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success(`Company ${action}d successfully`);
        fetchData();
      } else {
        toast.error('Action failed');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/superadmin/companies/${selectedCompany.id}/subscription`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(subscriptionForm)
        }
      );

      if (response.ok) {
        toast.success('Subscription updated successfully!');
        setShowSubscriptionDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update subscription');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handleApplyDiscount = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/superadmin/companies/${selectedCompany.id}/discount`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(discountForm)
        }
      );

      if (response.ok) {
        toast.success('Discount applied successfully!');
        setShowDiscountDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to apply discount');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handleUpdateCustomFeatures = async (customFeatures) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/superadmin/companies/${selectedCompany.id}/custom-features`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ custom_features: customFeatures })
        }
      );

      if (response.ok) {
        toast.success('Custom features updated successfully!');
        setShowCustomFeaturesDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update custom features');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const openSubscriptionDialog = (company) => {
    setSelectedCompany(company);
    setSubscriptionForm({
      plan: company.plan || 'starter',
      billing_cycle: company.billing_cycle || 'monthly'
    });
    setShowSubscriptionDialog(true);
  };

  const openDiscountDialog = (company) => {
    setSelectedCompany(company);
    setDiscountForm({
      discount_percentage: company.discount_percentage || 0
    });
    setShowDiscountDialog(true);
  };

  const openAdminDialog = async (company) => {
    setSelectedCompany(company);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/superadmin/companies/${company.id}/admin`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAdminDetails(data.admin);
        setAdminForm({
          new_email: data.admin.email,
          new_password: '',
          new_name: data.admin.name,
          new_phone: data.admin.phone || ''
        });
        setShowAdminDialog(true);
      } else {
        toast.error('Failed to load admin details');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    // Only send fields that have been changed
    const updates = {};
    if (adminForm.new_email !== adminDetails.email) updates.new_email = adminForm.new_email;
    if (adminForm.new_password) updates.new_password = adminForm.new_password;
    if (adminForm.new_name !== adminDetails.name) updates.new_name = adminForm.new_name;
    if (adminForm.new_phone !== (adminDetails.phone || '')) updates.new_phone = adminForm.new_phone;
    
    if (Object.keys(updates).length === 0) {
      toast.error('No changes to save');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/superadmin/companies/${selectedCompany.id}/admin`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );

      if (response.ok) {
        toast.success('Admin credentials updated successfully!');
        if (updates.new_password) {
          toast.info('New password has been set');
        }
        setShowAdminDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update admin');
      }
    } catch (error) {
      toast.error('Connection error');
      console.error(error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard" data-testid="superadmin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div>
          <h1 data-testid="dashboard-title">Super Admin Dashboard</h1>
          <p data-testid="user-email">{user.email}</p>
        </div>
        <div className="header-actions">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-company-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Company
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Company</DialogTitle>
                <DialogDescription>
                  Create a new company and invite a Company Admin
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCompany} className="space-y-4">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    required
                    value={createForm.company_name}
                    onChange={(e) => setCreateForm({...createForm, company_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="admin_name">Admin Name</Label>
                  <Input
                    id="admin_name"
                    required
                    value={createForm.admin_name}
                    onChange={(e) => setCreateForm({...createForm, admin_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="admin_email">Admin Email</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    required
                    value={createForm.admin_email}
                    onChange={(e) => setCreateForm({...createForm, admin_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="admin_password">Admin Password</Label>
                  <Input
                    id="admin_password"
                    type="password"
                    required
                    value={createForm.admin_password}
                    onChange={(e) => setCreateForm({...createForm, admin_password: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="admin_phone">Admin Phone (Optional)</Label>
                  <Input
                    id="admin_phone"
                    value={createForm.admin_phone}
                    onChange={(e) => setCreateForm({...createForm, admin_phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="plan">Initial Plan</Label>
                  <Select
                    value={createForm.plan}
                    onValueChange={(value) => setCreateForm({...createForm, plan: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SUBSCRIPTION_PLANS).map(([key, plan]) => (
                        <SelectItem key={key} value={key}>
                          {plan.name} - {typeof plan.price === 'number' ? `$${plan.price}/mo` : plan.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">Create Company</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Button onClick={handleLogout} variant="outline" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Analytics Cards */}
      {analytics && (
        <div className="analytics-grid">
          <Card data-testid="total-companies-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Total Companies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value" data-testid="total-companies-value">{analytics.total_companies}</div>
            </CardContent>
          </Card>

          <Card data-testid="total-users-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value" data-testid="total-users-value">{analytics.total_users}</div>
            </CardContent>
          </Card>

          <Card data-testid="active-subscriptions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Active Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value" data-testid="active-subscriptions-value">{analytics.active_subscriptions}</div>
            </CardContent>
          </Card>

          <Card data-testid="total-revenue-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value" data-testid="total-revenue-value">${analytics.total_revenue.toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Companies and Signups */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="companies">
            <Building2 className="w-4 h-4 mr-2" />
            Companies ({companies.length})
          </TabsTrigger>
          <TabsTrigger value="signups">
            <UserPlus className="w-4 h-4 mr-2" />
            Recent Signups ({signups.length})
          </TabsTrigger>
          <TabsTrigger value="pwa-stats" data-testid="pwa-stats-tab">
            📱 PWA & Notifications
          </TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies">
          <Card className="companies-card">
            <CardHeader>
              <CardTitle data-testid="companies-table-title">All Companies</CardTitle>
              <CardDescription>Manage subscriptions, apply discounts, and control access</CardDescription>
            </CardHeader>
            <CardContent>
          <div className="table-container">
            <table className="companies-table" data-testid="companies-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Plan</th>
                  <th>Billing Cycle</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Next Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company, index) => (
                  <tr key={company.id} data-testid={`company-row-${index}`}>
                    <td data-testid={`company-name-${index}`}>{company.name}</td>
                    <td data-testid={`company-plan-${index}`}>
                      <Badge variant="outline">
                        {SUBSCRIPTION_PLANS[company.plan]?.name || company.plan}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant="secondary">
                        {BILLING_CYCLES[company.billing_cycle] || 'Monthly'}
                      </Badge>
                    </td>
                    <td>
                      ${company.subscription_amount?.toFixed(2) || '0.00'}
                      {company.discount_percentage > 0 && (
                        <span className="discount-badge"> (-{company.discount_percentage}%)</span>
                      )}
                    </td>
                    <td data-testid={`company-status-${index}`}>
                      <Badge
                        variant={
                          company.subscription_status === 'active' ? 'success' : 
                          company.subscription_status === 'grace_period' ? 'warning' :
                          'destructive'
                        }
                      >
                        {company.subscription_status}
                      </Badge>
                    </td>
                    <td>
                      {company.next_payment_date ? 
                        new Date(company.next_payment_date).toLocaleDateString() : 
                        '-'
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openSubscriptionDialog(company)}
                          title="Edit Subscription"
                          className="action-btn"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Plan
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAdminDialog(company)}
                          title="Edit Admin Credentials"
                          className="action-btn"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Admin
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDiscountDialog(company)}
                          title="Apply Discount"
                          className="action-btn"
                        >
                          <Percent className="w-4 h-4 mr-1" />
                          Discount
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCompany(company);
                            setShowCustomFeaturesDialog(true);
                          }}
                          title="Custom Features"
                          className="action-btn"
                        >
                          ⚙️ Features
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePauseResume(company.id, company.subscription_status)}
                          data-testid={`pause-resume-btn-${index}`}
                          className="action-btn"
                        >
                          {company.subscription_status === 'grace_period' || company.subscription_status === 'suspended' ? (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              Resume
                            </>
                          ) : (
                            <>
                              <Pause className="w-4 h-4 mr-1" />
                              Pause
                            </>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Signups Tab */}
        <TabsContent value="signups">
          <Card>
            <CardHeader>
              <CardTitle>Recent Signups</CardTitle>
              <CardDescription>View all new user registrations and trial signups</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="table-container">
                <table className="companies-table">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Admin Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Selected Plan</th>
                      <th>Trial Plan</th>
                      <th>Signup Date</th>
                      <th>Trial End</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signups.length === 0 ? (
                      <tr>
                        <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                          No signups yet
                        </td>
                      </tr>
                    ) : (
                      signups.map((signup, index) => (
                        <tr key={signup.id || index}>
                          <td className="font-semibold">{signup.company_name}</td>
                          <td>{signup.admin_name}</td>
                          <td>{signup.admin_email}</td>
                          <td>{signup.admin_phone}</td>
                          <td>
                            <Badge variant="outline">
                              {SUBSCRIPTION_PLANS[signup.selected_plan]?.name || signup.selected_plan}
                            </Badge>
                          </td>
                          <td>
                            <Badge variant="default">
                              {signup.trial_plan === 'big_business' ? 'Big Business' : signup.trial_plan}
                            </Badge>
                          </td>
                          <td>{new Date(signup.signup_date).toLocaleDateString()}</td>
                          <td>{new Date(signup.trial_end_date).toLocaleDateString()}</td>
                          <td>
                            <Badge variant={signup.status === 'trial' ? 'secondary' : 'default'}>
                              {signup.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PWA Stats Tab */}
        <TabsContent value="pwa-stats">
          <Card>
            <CardHeader>
              <CardTitle data-testid="pwa-stats-title">📱 PWA & Push Notification Statistics</CardTitle>
              <CardDescription>Track mobile app installations and push notification subscriptions</CardDescription>
            </CardHeader>
            <CardContent>
              {pwaStats ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <p className="text-sm text-indigo-600 font-medium">Total PWA Installs</p>
                      <p className="text-3xl font-bold text-indigo-700" data-testid="total-pwa-installs">
                        {pwaStats.total_pwa_installs || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-600 font-medium">Push Subscriptions</p>
                      <p className="text-3xl font-bold text-purple-700" data-testid="total-push-subs">
                        {pwaStats.total_push_subscriptions || 0}
                      </p>
                    </div>
                  </div>

                  {/* Status Breakdown */}
                  {pwaStats.status_breakdown && Object.keys(pwaStats.status_breakdown).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">Install Status Breakdown</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(pwaStats.status_breakdown).map(([status, count]) => (
                          <Badge key={status} variant="outline" className="px-3 py-1">
                            {status.replace('_', ' ')}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* By Company Table */}
                  {pwaStats.by_company && pwaStats.by_company.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">PWA Stats by Company</h4>
                      <div className="table-container">
                        <table className="companies-table">
                          <thead>
                            <tr>
                              <th>Company</th>
                              <th>PWA Installs</th>
                              <th>Push Subscriptions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pwaStats.by_company.map((company, idx) => (
                              <tr key={idx}>
                                <td>{company.company_name || 'Unknown'}</td>
                                <td>
                                  <Badge variant="secondary">
                                    {company.installed || 0} installed
                                  </Badge>
                                </td>
                                <td>
                                  <Badge variant="outline">
                                    {company.push_subscriptions || 0} subscribers
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {pwaStats.by_company?.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No PWA install data yet.</p>
                      <p className="text-sm">Stats will appear once users start installing the app.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Loading PWA statistics...
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Subscription Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Subscription</DialogTitle>
            <DialogDescription>
              Update subscription plan and billing cycle for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateSubscription} className="space-y-4">
            <div>
              <Label htmlFor="plan">Subscription Plan</Label>
              <Select
                value={subscriptionForm.plan}
                onValueChange={(value) => setSubscriptionForm({...subscriptionForm, plan: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SUBSCRIPTION_PLANS).map(([key, plan]) => (
                    <SelectItem key={key} value={key}>
                      {plan.name} - {typeof plan.price === 'number' ? `$${plan.price}/mo` : plan.price} (Up to {plan.limit} employees)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="billing_cycle">Billing Cycle</Label>
              <Select
                value={subscriptionForm.billing_cycle}
                onValueChange={(value) => setSubscriptionForm({...subscriptionForm, billing_cycle: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BILLING_CYCLES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full">Update Subscription</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Discount</DialogTitle>
            <DialogDescription>
              Set discount percentage for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApplyDiscount} className="space-y-4">
            <div>
              <Label htmlFor="discount_percentage">Discount Percentage (0-100)</Label>
              <Input
                id="discount_percentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                required
                value={discountForm.discount_percentage}
                onChange={(e) => setDiscountForm({...discountForm, discount_percentage: parseFloat(e.target.value)})}
              />
            </div>
            <Button type="submit" className="w-full">Apply Discount</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Company Admin</DialogTitle>
            <DialogDescription>
              Update admin credentials for {selectedCompany?.name}
            </DialogDescription>
          </DialogHeader>
          {adminDetails && (
            <form onSubmit={handleUpdateAdmin} className="space-y-4">
              <div>
                <Label htmlFor="admin_email">Admin Email</Label>
                <Input
                  id="admin_email"
                  type="email"
                  value={adminForm.new_email}
                  onChange={(e) => setAdminForm({...adminForm, new_email: e.target.value})}
                />
                <p className="text-sm text-gray-500 mt-1">Current: {adminDetails.email}</p>
              </div>
              <div>
                <Label htmlFor="admin_name">Admin Name</Label>
                <Input
                  id="admin_name"
                  value={adminForm.new_name}
                  onChange={(e) => setAdminForm({...adminForm, new_name: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="admin_phone">Admin Phone</Label>
                <Input
                  id="admin_phone"
                  value={adminForm.new_phone}
                  onChange={(e) => setAdminForm({...adminForm, new_phone: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="admin_password">New Password (leave blank to keep current)</Label>
                <Input
                  id="admin_password"
                  type="password"
                  placeholder="Enter new password (min 8 characters)"
                  value={adminForm.new_password}
                  onChange={(e) => setAdminForm({...adminForm, new_password: e.target.value})}
                />
                <p className="text-sm text-gray-500 mt-1">Only fill this if you want to reset the password</p>
              </div>
              <Button type="submit" className="w-full">Update Admin</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Features Dialog */}
      <Dialog open={showCustomFeaturesDialog} onOpenChange={setShowCustomFeaturesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Custom Features - {selectedCompany?.name}</DialogTitle>
            <DialogDescription>
              <div className="space-y-2 mt-2">
                <p><strong>Current Plan:</strong> {selectedCompany && SUBSCRIPTION_PLANS[selectedCompany.plan]?.name}</p>
                <p className="text-sm text-gray-600">Enable or disable specific features for this company based on negotiated pricing</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          {selectedCompany && <CustomFeaturesManager 
            company={selectedCompany} 
            onSave={handleUpdateCustomFeatures}
            availableFeatures={AVAILABLE_FEATURES}
          />}
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .dashboard {
          min-height: 100vh;
          background: #f8fafc;
          padding: 2rem;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .dashboard-header h1 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .dashboard-header p {
          color: #64748b;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
        }

        .analytics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #667eea;
        }

        .companies-card {
          margin-bottom: 2rem;
        }

        .table-container {
          overflow-x: auto;
        }

        .companies-table {
          width: 100%;
          border-collapse: collapse;
        }

        .companies-table th {
          text-align: left;
          padding: 1rem;
          background: #f8fafc;
          font-weight: 600;
          color: #475569;
          border-bottom: 2px solid #e2e8f0;
        }

        .companies-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .action-btn {
          min-width: 90px;
          font-size: 0.875rem;
        }

        .discount-badge {
          color: #10b981;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: #64748b;
        }

        /* ====================================
           MOBILE RESPONSIVE STYLES
           ==================================== */
        
        @media (max-width: 768px) {
          .dashboard {
            padding: 0.5rem;
          }

          .dashboard-header {
            flex-direction: column;
            gap: 0.75rem;
            text-align: center;
            padding: 1rem;
          }

          .dashboard-header h1 {
            font-size: 1.5rem;
          }

          .dashboard-header p {
            font-size: 0.875rem;
          }

          .header-actions {
            flex-direction: column;
            width: 100%;
            gap: 0.5rem;
          }

          .header-actions button {
            width: 100%;
          }

          /* Analytics Grid */
          .analytics-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem;
            padding: 0 0.5rem;
          }

          .stat-value {
            font-size: 1.75rem !important;
          }

          /* Companies Card */
          .companies-card {
            margin: 0 0.5rem;
          }

          /* Table - Horizontal Scroll */
          .table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .companies-table {
            min-width: 800px;
            font-size: 0.8125rem;
          }

          .companies-table th,
          .companies-table td {
            padding: 0.5rem !important;
            font-size: 0.8125rem !important;
          }

          /* Action Buttons */
          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
            align-items: stretch;
          }

          .action-btn {
            width: 100%;
            min-width: auto;
            font-size: 0.8125rem;
            padding: 0.5rem !important;
          }

          /* Dialogs */
          .dialog-content {
            max-width: 95vw !important;
            max-height: 90vh !important;
            overflow-y: auto;
            padding: 1rem !important;
          }

          .dialog-title {
            font-size: 1.25rem !important;
          }

          .dialog-description {
            font-size: 0.875rem !important;
          }

          /* Forms */
          form .space-y-4 > * + * {
            margin-top: 1rem !important;
          }

          input, select, textarea {
            font-size: 16px !important; /* Prevents iOS zoom */
            min-height: 44px !important;
            padding: 0.75rem !important;
          }

          label {
            font-size: 0.875rem !important;
            margin-bottom: 0.25rem !important;
          }

          button {
            min-height: 44px !important;
            padding: 0.75rem 1rem !important;
          }

          /* Create Company Form */
          .space-y-4 {
            gap: 1rem;
          }

          /* Badge */
          .badge {
            font-size: 0.75rem !important;
            padding: 0.25rem 0.5rem !important;
          }

          .discount-badge {
            font-size: 0.75rem !important;
          }
        }

        @media (max-width: 375px) {
          .dashboard {
            padding: 0.25rem;
          }

          .dashboard-header h1 {
            font-size: 1.25rem !important;
          }

          .analytics-grid {
            padding: 0 0.25rem;
          }

          .stat-value {
            font-size: 1.5rem !important;
          }

          .companies-card {
            margin: 0 0.25rem;
          }

          .companies-table {
            font-size: 0.75rem;
          }

          .companies-table th,
          .companies-table td {
            padding: 0.375rem !important;
            font-size: 0.75rem !important;
          }

          .action-btn {
            font-size: 0.75rem;
            padding: 0.375rem !important;
          }
        }

        /* Landscape Mode */
        @media (max-height: 500px) and (orientation: landscape) {
          .dashboard-header {
            padding: 0.5rem;
          }

          .dashboard-header h1 {
            font-size: 1.25rem !important;
          }

          .analytics-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}

// Custom Features Manager Component
function CustomFeaturesManager({ company, onSave, availableFeatures }) {
  const [selectedFeatures, setSelectedFeatures] = useState(company.custom_features || []);

  // Get features included in the plan by default
  const getPlanFeatures = (plan) => {
    const tiers = ['starter', 'small_business', 'mid_size', 'big_business', 'enterprise'];
    const planIndex = tiers.indexOf(plan);
    const includedFeatures = [];
    
    Object.entries(availableFeatures).forEach(([key, feature]) => {
      const featureTierIndex = tiers.indexOf(feature.tier);
      if (featureTierIndex <= planIndex) {
        includedFeatures.push(key);
      }
    });
    
    return includedFeatures;
  };

  const planFeatures = getPlanFeatures(company.plan);
  
  const handleToggleFeature = (featureKey) => {
    if (selectedFeatures.includes(featureKey)) {
      setSelectedFeatures(selectedFeatures.filter(f => f !== featureKey));
    } else {
      setSelectedFeatures([...selectedFeatures, featureKey]);
    }
  };

  const isFeatureIncludedInPlan = (featureKey) => {
    return planFeatures.includes(featureKey);
  };

  const isFeatureEnabled = (featureKey) => {
    return isFeatureIncludedInPlan(featureKey) || selectedFeatures.includes(featureKey);
  };

  const handleSave = () => {
    onSave(selectedFeatures);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-1 gap-4">
        {Object.entries(availableFeatures).map(([key, feature]) => {
          const includedInPlan = isFeatureIncludedInPlan(key);
          const isEnabled = isFeatureEnabled(key);
          const isCustom = selectedFeatures.includes(key) && !includedInPlan;
          
          return (
            <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  disabled={includedInPlan}
                  onChange={() => handleToggleFeature(key)}
                  className="w-4 h-4"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{feature.name}</span>
                    {includedInPlan && (
                      <Badge variant="default" className="text-xs">
                        Included in Plan
                      </Badge>
                    )}
                    {isCustom && (
                      <Badge variant="secondary" className="text-xs">
                        Custom Add-on
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Default tier: {feature.tier}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold mb-2">Summary</h4>
        <p className="text-sm text-gray-600">Plan features: {planFeatures.length}</p>
        <p className="text-sm text-gray-600">
          Custom add-ons: {selectedFeatures.filter(f => !planFeatures.includes(f)).length}
        </p>
      </div>
      
      <Button onClick={handleSave} className="w-full">
        Save Custom Features
      </Button>
    </div>
  );
}
